import { useState, useCallback } from 'react';
import { teamApi } from '../api/team';
import type { TeamMember } from '../api/team';

export function useTeam() {
  const [members, setMembers]   = useState<TeamMember[]>([]);
  const [loading, setLoading]   = useState(false);
  const [actionId, setActionId] = useState<number | null>(null);
  const [error, setError]       = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await teamApi.list();
      setMembers(res.data.data);
    } catch {
      setError('Failed to load team members.');
    } finally {
      setLoading(false);
    }
  }, []);

  const invite = useCallback(async (data: { name: string; email: string; role: string }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await teamApi.invite(data);
      setMembers((prev) => [...prev, res.data.data]);
      return res.data.data;
    } catch (err: any) {
      const msg = err?.message ?? 'Invitation failed.';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const resendInvite = useCallback(async (id: number) => {
    setActionId(id);
    try {
      await teamApi.resendInvite(id);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to resend invitation.');
    } finally {
      setActionId(null);
    }
  }, []);

  const updateRole = useCallback(async (id: number, role: string) => {
    setActionId(id);
    try {
      const res = await teamApi.updateRole(id, role);
      setMembers((prev) => prev.map((m) => (m.id === id ? res.data.data : m)));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update role.');
    } finally {
      setActionId(null);
    }
  }, []);

  const suspend = useCallback(async (id: number) => {
    setActionId(id);
    try {
      const res = await teamApi.suspend(id);
      setMembers((prev) => prev.map((m) => (m.id === id ? res.data.data : m)));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to suspend member.');
    } finally {
      setActionId(null);
    }
  }, []);

  const reactivate = useCallback(async (id: number) => {
    setActionId(id);
    try {
      const res = await teamApi.reactivate(id);
      setMembers((prev) => prev.map((m) => (m.id === id ? res.data.data : m)));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to reactivate member.');
    } finally {
      setActionId(null);
    }
  }, []);

  const remove = useCallback(async (id: number) => {
    setActionId(id);
    try {
      await teamApi.remove(id);
      setMembers((prev) => prev.filter((m) => m.id !== id));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to remove member.');
    } finally {
      setActionId(null);
    }
  }, []);

  return {
    members, loading, actionId, error,
    fetchMembers, invite, resendInvite, updateRole, suspend, reactivate, remove, setError,
  };
}
